import { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  useTheme,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";

// Theme
import { tokens } from "../../theme";

// Icons
import { Check, Close } from "@mui/icons-material";

// Supabase
import supabase from "../../supabaseClient";

// Context
import { useNotification } from "../utils/Notification";
import { trackEvent } from "../utils/Utils";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "24px",
  boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  overflowY: "auto",
  p: { xs: 3, md: 5 },
};

export default function Plans({ open, onClose }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [planData, setPlanData] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [stripePortal, setStripePortal] = useState("");

  // Context
  const { showNotification } = useNotification();

  useEffect(() => {
    const fetchPlans = async () => {
      const { data, error } = await supabase
        .from("plans")
        .select("*")
        .order("monthly_price_usd", { ascending: true });
      if (error) {
        console.error(t("plans.errorFetchingPlans"), error);
      } else {
        const transformedData = data.map((plan) => ({
          name: plan.name,
          monthlyCost: `US$${Number(plan.monthly_price_usd).toFixed(2)}/month`,
          yearlyCost: `US$${(Number(plan.yearly_price_usd) / 12).toFixed(
            2
          )}/month`,
          benefits: [
            { label: t("plans.benefits.unlimitedContacts"), value: true },
            {
              label:
                plan.name === "Free"
                  ? `${t("plans.benefits.messagesMonthlyLimit")} ${t(
                    "plans.withKoubiLabel"
                  )}`
                  : t("plans.benefits.messagesMonthlyLimit"),
              value:
                plan.messages_monthly_limit === 1000000
                  ? "∞"
                  : plan.messages_monthly_limit,
            },
            {
              label: t("plans.benefits.templates"),
              value:
                plan.templates_limit === 1000000 ? "∞" : plan.templates_limit,
            },
            { label: t("plans.benefits.files"), value: plan.attachments_limit },
            { label: t("plans.benefits.variablesInMessages"), value: true },
            { label: t("plans.benefits.labelsInMessages"), value: true },
            { label: t("plans.benefits.selfReminders"), value: true },
            { label: t("plans.benefits.ai"), value: plan.ai },
            {
              label: t("plans.benefits.importContacts"),
              value: plan.import_contacts_csv,
            },
            {
              label: t("plans.benefits.importContactsWhatsApp"),
              value: plan.import_contacts_whatsapp,
            },
            {
              label: t("plans.benefits.automaticBirthdayMessages"),
              value: plan.birthday_messages,
            },
            {
              label: t("plans.benefits.groupBulkMessages"),
              value: plan.group_messages,
            },
            {
              label: t("plans.benefits.recurringMessages"),
              value: plan.recurring_messages,
            },
            { label: t("plans.benefits.koubiApi"), value: plan.api },
            {
              label: t("plans.benefits.premiumSupport"),
              value: plan.premium_support,
            },
          ],
        }));

        setPlanData(transformedData);
      }
    };

    const fetchStripePortal = async () => {
      const { data: teamData, error: teamError } = await supabase
        .from("teams")
        .select("stripe_id, subscription_id")
        .single();

      if (teamError) {
        return;
      }

      const body = {
        customerId: teamData.stripe_id,
        returnUrl: `${window.location.origin}/dashboard/subscription`,
      };

      const stripeResponse = await fetch(
        "https://stripecustomerportal-tiph5o4x3q-uc.a.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const stripeData = await stripeResponse.json();

      setStripePortal(
        `${stripeData.url}/subscriptions/${teamData.subscription_id}/update`
      );
    };

    fetchPlans();
    fetchStripePortal();
  }, []);

  if (planData === null) {
    return (
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            ...style,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </Modal>
    );
  }

  const handleBillingPeriodChange = (event, newBillingPeriod) => {
    trackEvent("plans-billing_period_change", { period: newBillingPeriod });
    if (newBillingPeriod !== null) {
      setBillingPeriod(newBillingPeriod);
    }
  };

  const handlePlanSelection = (planName) => {
    trackEvent("plans-plan_selected", { plan: planName });
    setSelectedPlan(planName);
    if (!stripePortal) {
      showNotification(t("plans.pageLoading"));
      return;
    }
    window.open(stripePortal, "_blank");
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <ToggleButtonGroup
          color="secondary"
          value={billingPeriod}
          exclusive
          onChange={handleBillingPeriodChange}
          sx={{ mb: 2, display: "flex", justifyContent: "center" }}
        >
          <ToggleButton value="monthly" sx={{ fontWeight: "bold" }}>
            {t("plans.billingPeriod.monthly")}
          </ToggleButton>
          <ToggleButton value="yearly" sx={{ fontWeight: "bold" }}>
            {t("plans.billingPeriod.yearly")}
          </ToggleButton>
        </ToggleButtonGroup>
        <Grid container spacing={2}>
          {planData.map((plan) => (
            <Grid item xs={12} sm={6} md={3} key={plan.name}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor:
                    plan.name === "Pro"
                      ? colors.blueAccent[700]
                      : plan.name === "Premium"
                        ? colors.greenAccent[700]
                        : "background.default",
                  border:
                    selectedPlan === plan.name
                      ? `2px solid ${colors.primary[500]}`
                      : "none",
                }}
                onClick={() => handlePlanSelection(plan.name)}
              >
                <CardActionArea>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="h3"
                      component="div"
                      fontWeight="bold"
                      gutterBottom
                      textAlign="center"
                    >
                      {plan.name}
                    </Typography>
                    <Typography
                      variant="h5"
                      component="div"
                      fontWeight="bold"
                      gutterBottom
                      textAlign="center"
                    >
                      {billingPeriod === "monthly"
                        ? plan.monthlyCost
                        : plan.yearlyCost}
                    </Typography>
                    <Grid container spacing={1}>
                      {plan.benefits
                        .sort((a, b) => b.value - a.value)
                        .map((benefit) => (
                          <Grid item xs={12} key={benefit.label}>
                            <Box display="flex" alignItems="center">
                              {benefit.value === "∞" || benefit.value ? (
                                <Check sx={{ color: "green", mr: 1 }} />
                              ) : (
                                <Close sx={{ color: "red", mr: 1 }} />
                              )}
                              <Typography
                                variant="body2"
                                color={
                                  plan.name === "Pro"
                                    ? colors.primary[100]
                                    : plan.name === "Premium"
                                      ? colors.primary[100]
                                      : ""
                                }
                              >
                                {benefit.label}
                                {(typeof benefit.value === "number" ||
                                  benefit.value === "∞") &&
                                  benefit.value !== 0 &&
                                  `: ${benefit.value}`}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                    </Grid>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Modal>
  );
}
